import { useTypeSafeMutation } from '@oresundsbron/api';
import { pipe } from 'fp-ts/lib/function';
import { FC, useCallback, useState } from 'react';
import { Lens } from 'monocle-ts';
import { ITicketPayement } from '../reepay';
import {
  setError,
  setResponse,
  usePrivateITicketData,
} from './stores/usePrivateITicketData';
import {
  fromNullable,
  fromPredicate,
  getOrElse,
  map,
  match,
} from 'fp-ts/lib/Option';
import {
  PrivateITicketOrderInputV2,
  PrivateITicketOrderResponse,
  PrivateITicketOrderWithPaymentInputV2,
  constants,
} from '@oresundsbron/validators';
import { isBackendError } from '../../lib/errors';
import { Box } from '@oresundsbron/bridge-ui';
import { tapO } from '@oresundsbron/utilities';
import * as gtag from '~/lib/gtag';
import { head, map as mapA, reduce } from 'fp-ts/lib/Array';
import { getDefaultCurrency } from '~/lib/currency';
import { useRouter } from 'next/router';

const sessionData =
  Lens.fromProp<PrivateITicketOrderWithPaymentInputV2>()('session');
const cardData = Lens.fromProp<PrivateITicketOrderWithPaymentInputV2>()('data');

const setPaymentInformation =
  (resp: Reepay.Response, session: string) =>
  (data?: PrivateITicketOrderInputV2) =>
    pipe(
      data as PrivateITicketOrderWithPaymentInputV2,
      fromNullable,
      map(sessionData.set(session)),
      map(cardData.set({ ...resp, subscription: '', error: '' }))
    );

const onError = (e: unknown) =>
  pipe(
    e,
    fromPredicate(isBackendError),
    match(
      () => {},
      (e) => setError(e)
    )
  );

const onSuccess =
  (body: PrivateITicketOrderInputV2, locale: string) => (data: unknown) => {
    const response = data as PrivateITicketOrderResponse;
    setResponse(response);

    let ticketType: string;
    switch (response.ticketType) {
      case 'Campaign: Day Pass':
        ticketType = constants.DayTicket;
        break;
      case 'Campaign: Weekend':
        ticketType = constants.WeekendTicket;
        break;
      default:
        ticketType = constants.OnlineTicket;
    }
    const { tickets } = response;
    gtag.resetPurchase();

    gtag.purchase(
      {
        transaction_id: pipe(
          tickets,
          head,
          map((ticket) => ticket.contractNo),
          getOrElse(() => '')
        ),
        value: pipe(
          tickets,
          mapA((ticket) => ticket.price),
          reduce(0, (res, price) => res + price),
          (totalPrice) => `${totalPrice}`
        ),
        currency: getDefaultCurrency(locale),
        coupon: body.subscribe === 'true' ? 'ClubØresund' : undefined,
        items: [
          {
            item_name: ticketType,
            item_id: body.vehicleOption.split('-')[0],
            item_category: 'Private',
            item_variant: 'OperatorOnlineTicket',
            quantity: `${tickets.length}`,
            price: pipe(
              tickets,
              head,
              map((ticket) => `${ticket.price}`),
              getOrElse(() => '')
            ),
          },
        ],
      },
      {
        email: body.email,
        firstName: body.firstName,
        lastName: body.lastName,
        phone: ``,
        city: '',
        address1: '',
        zipCode: '',
        company: '',
        country: body.country,
      }
    );
    body.subscribe && gtag.subscribeToNewsLetterOnPurchase(ticketType);
  };

export const CreditCardPayment: FC = () => {
  const { data } = usePrivateITicketData();
  const { locale } = useRouter();
  const [loading, toggleLoading] = useState(false);
  const { mutateAsync } = useTypeSafeMutation(
    ['iticket'],
    (req) => req.ITicket.orderPrivate,
    {
      onError,
      onSuccess: onSuccess(
        data as PrivateITicketOrderInputV2,
        locale as string
      ),
      onSettled: () => toggleLoading(false),
    }
  );

  const _onSuccess = useCallback(
    (resp: Reepay.Response, session: string) =>
      pipe(
        data,
        setPaymentInformation(resp, session),
        tapO(() => toggleLoading(true)),
        map(mutateAsync),
        match(
          () => {},
          () => {}
        )
      ),
    [data, mutateAsync]
  );

  if (loading) {
    return (
      <Box className="item-center col-span-full flex h-[600px] items-center justify-center text-5xl text-primary-600 md:col-span-6 md:col-start-4">
        <svg
          className="animate-spin"
          fill="currentColor"
          width="1em"
          height="1em"
          viewBox="0 0 24 24"
        >
          <path
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          ></path>
        </svg>
      </Box>
    );
  }

  return (
    <ITicketPayement order={data} onError={onError} onSuccess={_onSuccess} />
  );
};
